import { useEffect, useState } from 'react';

// Tailwind `md`: at/above it a pinned sidebar is a desktop rail, below it the
// same element is an off-canvas drawer (mirrors MainLayout's MD_BREAKPOINT).
const MD_BREAKPOINT = '(min-width: 768px)';

/**
 * Whether the viewport is at Tailwind `md` or wider. Guarded for jsdom, which
 * has no `matchMedia`: there it reports `false`, the phone case.
 */
export function useIsMdScreen(): boolean {
  const [isMd, setIsMd] = useState(
    () => typeof window.matchMedia === 'function' && window.matchMedia(MD_BREAKPOINT).matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(MD_BREAKPOINT);
    const handler = (e: MediaQueryListEvent) => setIsMd(e.matches);
    mql.addEventListener('change', handler);
    setIsMd(mql.matches);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return isMd;
}

/**
 * #2595: a closed drawer is off-canvas (`-translate-x-full`), which hides its
 * paint but not its participation — 140 to 154 links and buttons stayed in the
 * tab order on a 390 px phone. `inert` takes the whole subtree out of focus,
 * hit-testing and the accessibility tree. The sidebar is live when it is open
 * as a drawer, or when it is a pinned rail at `md` and wider (CSS shows it
 * there with `md:translate-x-0` whatever `isOpen` says).
 */
export function useDrawerInert(isOpen: boolean, pinned: boolean): boolean {
  const isMd = useIsMdScreen();
  return !isOpen && !(pinned && isMd);
}
